import { LAUNCH_BY_EXAM, isRetiredApp } from './appDisplayUtils';

/**
 * Owned-apps data for the /apps Panels page (DECISIONS.md section 3).
 *
 * `panelsFor(owned)` turns the `access.owned` payload (GET /api/apps/owned,
 * BUILD_PLAN C10) into the four panels. Ownership is the only input: no locked
 * tiles, no host or subject filtering. Every tile's `launch` comes from
 * LAUNCH_BY_EXAM, and a key without a launch target (a retired app, or a key
 * the map does not know) is dropped rather than shown dead.
 *
 * Study tools (Custom Quiz Generator, Flash Cards, Notes) are part of the
 * default plan every account holds (D:31), so they are listed whatever the
 * payload says; Flash Cards and Notes sit in Exam practice (Q19 default).
 * Reasoning and Applications follow `owned.tools`.
 */

export const PANEL_IDS = ['exam_practice', 'reasoning', 'applications', 'interviews'];

const PANEL_TITLES = {
  exam_practice: 'Exam practice',
  reasoning: 'Reasoning',
  applications: 'Applications',
  interviews: 'Interviews',
};

/** Exam keys in the order the pricing page sells them. */
const EXAM_ORDER = ['ucat', 'tmua', 'esat', 'lnat', 'tara'];

const STUDY_TOOLS = [
  { key: 'custom_quiz_generator', title: 'Custom Quiz Generator' },
  { key: 'flash_cards', title: 'Flash Cards' },
  { key: 'notes', title: 'Notes' },
];

const REASONING_APPS = [
  { key: 'verbal_reasoning', title: 'Verbal Reasoning' },
  { key: 'numerical_reasoning', title: 'Numerical Reasoning' },
  { key: 'logical_reasoning', title: 'Logical Reasoning' },
  { key: 'pattern_recognition', title: 'Pattern Recognition' },
];

const BUILDER_APPS = [
  { key: 'personal_statement', title: 'Personal Statement' },
  { key: 'cv', title: 'CV' },
  { key: 'cover_letter', title: 'Cover Letter' },
];

const PACKAGE_APPS = {
  medicine: [
    { key: 'live_interview_medicine', title: 'Medicine Interviews' },
    { key: 'medicine_mmi_interview', title: 'MMI' },
    { key: 'medicine_panel_interview', title: 'Panel' },
  ],
  dentistry: [{ key: 'live_interview_dentistry', title: 'Dentistry Interviews' }],
  oxbridge: [{ key: 'live_interview_oxbridge', title: 'Oxbridge Interviews' }],
  degree_apprenticeship: [
    { key: 'law_interview', title: 'Law Interview' },
    { key: 'computer_science_interview', title: 'Computer Science Interview' },
    { key: 'engineering_interview', title: 'Engineering Interview' },
    { key: 'investment_banking_interview', title: 'Investment Banking Interview' },
    { key: 'private_equity_interview', title: 'Private Equity Interview' },
    { key: 'operations_fs_interview', title: 'Operations Interview (FS)' },
    { key: 'interview_simulator', title: 'Interview Simulator' },
    { key: 'scholarship_interview', title: 'Scholarship Interview' },
    { key: 'oxbridge_interview', title: 'Oxbridge Interview' },
  ],
};

const PACKAGE_TITLES = {
  medicine: 'Medicine',
  dentistry: 'Dentistry',
  oxbridge: 'Oxbridge',
  degree_apprenticeship: 'Degree Apprenticeship',
};

const PACKAGE_ORDER = ['medicine', 'dentistry', 'oxbridge', 'degree_apprenticeship'];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/** A tile for an app key, or null when the key has no launch target. */
function tile(key, title) {
  const app = { exam_name: key, title };
  if (isRetiredApp(app)) return null;
  const launch = LAUNCH_BY_EXAM[key];
  if (!launch) return null;
  return { key, title, launch };
}

function tiles(list) {
  return list.map(({ key, title }) => tile(key, title)).filter(Boolean);
}

function examTiles(owned) {
  const held = new Set(
    asArray(owned.exams)
      .map((e) => String(e?.exam || '').toLowerCase().trim())
      .filter(Boolean),
  );
  return EXAM_ORDER.filter((key) => held.has(key))
    .map((key) => tile(key, key.toUpperCase()))
    .filter(Boolean);
}

function packageGroups(owned) {
  const interviews = owned.interviews || {};
  const held = asArray(interviews.packages).filter((p) => p && typeof p === 'object');
  const byPackage = new Map();
  held.forEach((p) => {
    if (!byPackage.has(p.package)) byPackage.set(p.package, []);
    byPackage.get(p.package).push(p);
  });

  const groups = [];
  PACKAGE_ORDER.forEach((pkg) => {
    const entries = byPackage.get(pkg);
    if (!entries) return;
    const apps = tiles(PACKAGE_APPS[pkg]);
    if (pkg === 'oxbridge') {
      // One group per held subject: a subject is fixed once held (T21).
      const subjects = entries.flatMap((e) => asArray(e.subjects));
      subjects
        .map((s) => String(s?.subject || '').trim())
        .filter(Boolean)
        .forEach((subject) => {
          groups.push({
            id: `oxbridge:${subject}`,
            title: `${PACKAGE_TITLES.oxbridge}, ${subject}`,
            package: pkg,
            subject,
            apps,
          });
        });
      return;
    }
    groups.push({ id: pkg, title: PACKAGE_TITLES[pkg], package: pkg, apps });
  });
  return groups;
}

function questionPackGroup(owned) {
  const packs = asArray(owned.interviews?.question_packs)
    .map((p) => String(p?.exam || '').trim())
    .filter(Boolean)
    .map((exam) => ({
      key: `question_pack:${exam}`,
      title: exam,
      launch: `/practice/interviews/${encodeURIComponent(exam)}`,
    }));
  if (packs.length === 0) return null;
  return { id: 'question_packs', title: 'Question packs', apps: packs };
}

function interviewsPanel(owned) {
  // The panel exists only when a package is owned (D:110). A pack held
  // without a package (a hand-made state) does not open it.
  const groups = packageGroups(owned);
  if (groups.length === 0) return null;
  const packs = questionPackGroup(owned);
  return {
    id: 'interviews',
    title: PANEL_TITLES.interviews,
    groups: packs ? [...groups, packs] : groups,
  };
}

/**
 * The four panels for an owned payload.
 *
 * Returns `{ status: 'error', panels: [] }` when `owned` is not an object
 * (the endpoint failed or the auth payload carried `owned: null`), so the page
 * can show its error state instead of an empty strip. Otherwise
 * `{ status: 'ok', panels }`; a panel with nothing in it is left out.
 */
export function panelsFor(owned) {
  if (!owned || typeof owned !== 'object' || Array.isArray(owned)) {
    return { status: 'error', panels: [] };
  }
  const tools = new Set(asArray(owned.tools));
  const panels = [];

  panels.push({
    id: 'exam_practice',
    title: PANEL_TITLES.exam_practice,
    apps: [...examTiles(owned), ...tiles(STUDY_TOOLS)],
  });
  if (tools.has('reasoning')) {
    panels.push({ id: 'reasoning', title: PANEL_TITLES.reasoning, apps: tiles(REASONING_APPS) });
  }
  if (tools.has('builders')) {
    panels.push({ id: 'applications', title: PANEL_TITLES.applications, apps: tiles(BUILDER_APPS) });
  }
  const interviews = interviewsPanel(owned);
  if (interviews) panels.push(interviews);

  return { status: 'ok', panels: panels.filter((p) => (p.apps ? p.apps.length > 0 : true)) };
}
